import React, { ReactNode } from 'react';
import AlertType from '@app/model/alert/AlertType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertInput from '@app/model/alert/input/AlertInput';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import AlertScheduleType from '@app/model/alert/AlertScheduleType';
import { useQuery } from '@tanstack/react-query';
import FormPlaceholder from '@app/component/placeholder/FormPlaceholder';
import AlertService from '@app/model/alert/service/AlertService';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import Message from '@app/component/message/Message';
import _isEmpty from 'lodash/isEmpty';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import PriceComparisonAlertRuleTrigger from '@app/model/alert/PriceComparisonAlertRuleTrigger';
import CompetitorsPriceRateAlertRuleInclusion from '@app/model/alert/CompetitorsPriceRateAlertRuleInclusion';
import { AlertRecipientsType } from '@app/model/alert/AlertRecipientsType';
import Refunding from '@app/model/market_state/Refunding';
import { CompetitorsSoldOutAlertRuleInclusion } from '@app/model/alert/CompetitorsSoldOutAlertRuleInclusion';
import ParityPriceComparisonAlertRuleDifferenceType
	from '@app/model/alert/ParityPriceComparisonAlertRuleDifferenceType';


const AlertFormInitializationFragment: React.FunctionComponent<{
	alertId?: number | undefined,
	alertType: AlertType,
	companyHotel: CompanyHotel,
	isCopy: boolean,
	renderForm: (companyHotel: CompanyHotel, defaultValues: AlertInput) => ReactNode
}> = ({ alertId, alertType, companyHotel, isCopy, renderForm }) => {

	return !!alertId ?
		<EditAlertForm alertId={alertId} alertType={alertType} companyHotel={companyHotel} isCopy={isCopy}
									 renderForm={renderForm} /> :
		<NewAlertForm alertType={alertType} companyHotel={companyHotel} isCopy={isCopy} renderForm={renderForm} />;
};

const EditAlertForm: React.FunctionComponent<{
	alertId: number,
	alertType: AlertType,
	companyHotel: CompanyHotel,
	isCopy: boolean,
	renderForm: (companyHotel: CompanyHotel, defaultValues: AlertInput) => ReactNode
}> = ({ alertId, alertType, companyHotel, isCopy, renderForm }) => {

	const { data: alertOutput, isSuccess, isLoading } = useQuery({
		queryFn: () => AlertService.findAlertById(companyHotel.id, alertId),
		queryKey: AlertQueryKeys.detail(alertId),
	});

	if (isLoading) return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8"><FormPlaceholder /></div>;

	if (isSuccess) {

		//remove pastPeriodInDays that is not selected by the user
		let competitorsPriceRateAlertRule;
		if (alertOutput.competitorsPriceRateAlertRule) {
			const { pastPeriodInDays, ...rest } = alertOutput.competitorsPriceRateAlertRule;
			competitorsPriceRateAlertRule = rest;
		}

		const defaultValues: AlertInput = {

			id: isCopy ? undefined : alertOutput.alert.id,
			companyHotelId: alertOutput.alert.companyHotelId,
			name: alertOutput.alert.name + (isCopy ? ' Copy' : ''),
			type: alertOutput.alert.type,
			filter: {
				//@ts-ignore
				roomTypes: alertOutput.alert.filter.roomTypes,
				refunding: alertOutput.alert.filter.refunding,
				lengthOfStay: '' +alertOutput.alert.filter.lengthOfStay,
				meal: alertOutput.alert.filter.meal,
			},
			schedule: {
				time: alertOutput.alert.schedule.time,
				type: alertOutput.alert.schedule.type,
				// @ts-ignore
				weekdays: _isEmpty(alertOutput.alert.schedule.weekdays) ? ['ALL'] : alertOutput.alert.schedule.weekdays,

			},
			timeframe: alertOutput.alert.timeframe,
			// @ts-ignore
			competitorsPriceRateAlertRuleInput: competitorsPriceRateAlertRule,
			priceComparisonAlertRuleInput: alertOutput.priceComparisonAlertRule,
			parityPriceComparisonAlertRuleInput: alertOutput.parityPriceComparisonAlertRule,
			competitorsSoldOutAlertRuleInput: alertOutput.competitorsSoldOutAlertRule,
			extraEmails: alertOutput.alert.extraEmails,
			recipientsType: alertOutput.alert.recipientsType,
			recipientProfileIds: alertOutput.alert.recipientProfileIds,
		};

		return renderForm(companyHotel, defaultValues);
	}

	return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
		<Message state="warning">Alert could not be found!</Message>
	</div>;
};

const NewAlertForm: React.FunctionComponent<{
	alertType: AlertType,
	companyHotel: CompanyHotel,
	isCopy: boolean,
	renderForm: (companyHotel: CompanyHotel, defaultValues: AlertInput) => ReactNode
}> = ({ alertType, companyHotel, isCopy, renderForm }) => {
	const defaultValues: AlertInput = {
		companyHotelId: companyHotel!.id,
		name: '',
		type: alertType,
		filter: {
			roomTypes: companyHotel.roomMappingTypeConfigurations!.filter(rt => rt.isEnabled && rt.type !== HotelRoomMappingType.NONE && rt.type !== HotelRoomMappingType.SINGLE)
				.map(rt => rt.type),
			refunding: Refunding.NONE,
			lengthOfStay: "1",
			meal: null,
		},
		// @ts-ignore
		schedule: {
			type: AlertScheduleType.TRIGGERED,
		},
		timeframe: {
			days: 7,
		},
		// @ts-ignore
		priceComparisonAlertRuleInput: {
			pricePoint: PriceComparisonAlertRulePricePoint.MEDIAN,
			trigger: PriceComparisonAlertRuleTrigger.INSIDE_PRICE_RANGE,
		},
		// @ts-ignore
		competitorsPriceRateAlertRuleInput: {
			competitorsInclusion: CompetitorsPriceRateAlertRuleInclusion.ALL,
		},
		// @ts-ignore
		competitorsSoldOutAlertRuleInput: {
			competitorsInclusion: CompetitorsSoldOutAlertRuleInclusion.ANY,
		},
		// @ts-ignore
		parityPriceComparisonAlertRuleInput: {
			differenceType: ParityPriceComparisonAlertRuleDifferenceType.AMOUNT,
		},
		extraEmails: [],
		recipientsType: AlertRecipientsType.NONE,
		recipientProfileIds: [],
	};
	return renderForm(companyHotel, defaultValues);
};

export default AlertFormInitializationFragment;
